import { useEffect, useMemo, useRef, useState } from 'react'
import {
  BookOpen,
  Check,
  Compass,
  Handshake,
  LibraryBig,
  Search,
  Share2,
  Trophy,
  UserRound,
} from 'lucide-react'
import {
  AccountSwitcher,
  ACTIVE_ACCOUNT_EVENT,
  type ActiveAccountDetail,
} from './features/accounts/AccountSwitcher'
import {
  clearInventoryQueueForUser,
  flushInventoryQueue,
  loadCards,
  saveCard,
  saveCardWithQueue,
} from './features/cards/api'
import {
  clearGuestInventory,
  hasGuestInventory,
  readGuestInventory,
  saveGuestCard,
} from './features/cards/guestInventory'
import {
  clearDeviceTokens,
  deleteUserAccount,
  exportUserData,
  getProfile,
  registerDeviceToken,
  saveProfile,
  unregisterDeviceToken,
  type UserProfile,
} from './features/profile/api'
import {
  createTradeRequest,
  cancelTradeRequest,
  createUserReport,
  listTradeMatches,
  listTradeRequests,
  respondTradeRequest,
  revealTradeContact,
  type TradeMatch,
  type TradeRequest,
} from './features/trade/api'
import { matchActivity, matchQuality } from './features/trade/presentation'
import { supabase } from './lib/supabase'
import { useI18n, type Locale } from './lib/i18n'
import { setAnalyticsPlatform, track } from './lib/analytics'
import { getAppConfig, versionBelow } from './lib/config'
import { useAuth } from './features/auth/AuthGate'
import { hasNativeBridge, sendNative, sendNativeAndWait } from './lib/nativeBridge'
import { pathForTab, tabFromPath, type AppTab } from './lib/navigation'

type CardItem = {
  id: number
  name: string
  category: string
  imagePath?: string | null
  owned: number
  wanted: number
  keep: number
  level: number
  updatedAt?: string
}

export function App() {
  const { locale, t } = useI18n()
  const { session, ready, openSignIn } = useAuth()
  const [tab, setTab] = useState<AppTab>(() => tabFromPath(window.location.pathname))
  const [cards, setCards] = useState<CardItem[]>([])
  const [cardsLoading, setCardsLoading] = useState(true)
  const [category, setCategory] = useState('all')
  const [query, setQuery] = useState('')
  const [activeAccountId, setActiveAccountId] = useState<string | null>(
    localStorage.getItem('clash_cards_active_account_id'),
  )
  const [syncMessage, setSyncMessage] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<'all' | 'owned' | 'missing' | 'dupe'>('all')
  const [tradeRefresh, setTradeRefresh] = useState(0)
  const [nativeBridgeReady, setNativeBridgeReady] = useState(hasNativeBridge())
  const [blockedReason, setBlockedReason] = useState<'maintenance' | 'upgrade' | null>(null)
  const [guestImport, setGuestImport] = useState<{
    accountId: string
    accountName?: string
    automatic: boolean
  } | null>(null)
  const guestImportInFlight = useRef<string | null>(null)
  const demoMode = new URLSearchParams(window.location.search).get('demo') === '1'
  const guestMode = !demoMode && ready && !session
  const filtered = cards.filter(
    (card) =>
      (category === 'all' || card.category === category) &&
      card.name.toLowerCase().includes(query.toLowerCase()) &&
      (statusFilter === 'all' ||
        (statusFilter === 'owned' && card.owned > 0) ||
        (statusFilter === 'missing' && card.owned === 0) ||
        (statusFilter === 'dupe' && card.owned > card.keep)),
  )
  const collected = cards.filter((card) => card.owned > 0).length
  const recentCard = cards
    .filter((card) => card.updatedAt)
    .sort((a, b) => Date.parse(b.updatedAt!) - Date.parse(a.updatedAt!))[0]

  useEffect(() => {
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<ActiveAccountDetail>).detail
      setActiveAccountId(detail.id)
      setCategory('all')
      setQuery('')
      setStatusFilter('all')
      if (detail.switched) {
        setSyncMessage(t('accountSwitched', { name: detail.name ?? t('thisAccount') }))
        window.setTimeout(() => setSyncMessage(null), 1600)
      }
      if (detail.id && hasGuestInventory())
        setGuestImport({
          accountId: detail.id,
          accountName: detail.name,
          automatic: Boolean(detail.created),
        })
    }
    window.addEventListener(ACTIVE_ACCOUNT_EVENT, listener)
    return () => window.removeEventListener(ACTIVE_ACCOUNT_EVENT, listener)
  }, [t])

  useEffect(() => {
    if (demoMode) return
    void getAppConfig()
      .then((config) => {
        if (!config) return
        setBlockedReason(
          config.maintenance_mode
            ? 'maintenance'
            : versionBelow(import.meta.env.VITE_APP_VERSION ?? '1.0.0', config.minimum_web_version)
              ? 'upgrade'
              : null,
        )
      })
      .catch(() => undefined)
  }, [demoMode])

  useEffect(() => {
    let cancelled = false
    setCardsLoading(true)
    if (demoMode || guestMode) {
      fetch('/catalog/cards.en.json')
        .then((response) => response.json())
        .then((catalog) => {
          if (cancelled) return
          setCards(
            catalog.cards.map(
              (card: {
                number: number
                type: string
                names: { en: string }
                entityId: number
              }) => ({
                id: card.number,
                name: card.names.en,
                category: (
                  {
                    elixir: 'Elixir Cards',
                    dark_elixir: 'Dark Elixir Cards',
                    builder_base: 'Builder Base Cards',
                    super_troop: 'Super Troop Cards',
                  } as Record<string, string>
                )[card.type],
                imagePath: `/cards/entities/${card.entityId}.png`,
                owned: demoMode && card.number <= 5 ? 1 : 0,
                wanted: demoMode && card.number > 55 ? 1 : 0,
                keep: 1,
                level: 1,
                ...(guestMode ? readGuestInventory()[card.number] : {}),
              }),
            ),
          )
          setCardsLoading(false)
        })
        .catch(() => {
          if (!cancelled) setCardsLoading(false)
        })
      return () => {
        cancelled = true
      }
    }
    if (!activeAccountId) {
      setCards([])
      setCardsLoading(false)
      return
    }
    setCards([])
    setSyncMessage(t('syncing'))
    loadCards(activeAccountId, locale)
      .then((next) => {
        if (!cancelled) setCards(next)
      })
      .then(() => {
        if (!cancelled) {
          setSyncMessage(null)
          setCardsLoading(false)
        }
      })
      .catch(() => {
        if (!cancelled) {
          setSyncMessage(t('syncFailed'))
          setCardsLoading(false)
        }
      })
    return () => {
      cancelled = true
    }
  }, [activeAccountId, demoMode, guestMode, locale, t])

  useEffect(() => {
    if (demoMode || guestMode) return
    if (!session) return
    const flush = () => {
      void flushInventoryQueue(session.user.id).then(({ sent }) => {
        if (sent) {
          setSyncMessage(t('synced', { count: sent }))
          window.setTimeout(() => setSyncMessage(null), 1600)
        }
      })
    }
    window.addEventListener('online', flush)
    flush()
    return () => window.removeEventListener('online', flush)
  }, [demoMode, guestMode, session, t])

  useEffect(() => {
    if (demoMode || guestMode) return
    const listener = (event: Event) => {
      const detail = (
        event as CustomEvent<{
          type?: string
          payload?: {
            token?: string
            platform?: 'ios' | 'android' | 'web'
            type?: string
            route?: string
          }
        }>
      ).detail
      if (detail.type === 'native_capabilities' && detail.payload?.platform) {
        setAnalyticsPlatform(detail.payload.platform)
        setNativeBridgeReady(true)
      }
      if (detail.type === 'device_token' && detail.payload?.token && detail.payload.platform)
        void registerDeviceToken(detail.payload.token, detail.payload.platform)
      if (detail.type === 'push_received' && detail.payload?.type === 'trade_request')
        setTradeRefresh((value) => value + 1)
    }
    window.addEventListener('clashcards:native', listener)
    sendNative('web_ready', { locale })
    return () => window.removeEventListener('clashcards:native', listener)
  }, [demoMode, guestMode, locale])

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' })
  }, [tab])
  useEffect(() => {
    const restore = () => setTab(tabFromPath(window.location.pathname))
    window.addEventListener('popstate', restore)
    return () => window.removeEventListener('popstate', restore)
  }, [])

  function navigate(next: AppTab) {
    if (next === tab) return
    window.history.pushState(
      null,
      '',
      pathForTab(next) + window.location.search + window.location.hash,
    )
    setTab(next)
  }

  async function updateCard(next: CardItem) {
    const timestamped = { ...next, updatedAt: new Date().toISOString() }
    setCards((current) => current.map((card) => (card.id === next.id ? timestamped : card)))
    if (guestMode)
      saveGuestCard(timestamped.id, {
        owned: timestamped.owned,
        wanted: timestamped.wanted,
        keep: timestamped.keep,
        level: timestamped.level,
        updatedAt: timestamped.updatedAt,
      })
    if (!demoMode && activeAccountId) {
      setSyncMessage(t('saving'))
      try {
        const result = await saveCardWithQueue(session?.user.id ?? '', activeAccountId, timestamped)
        track('inventory_updated', locale, activeAccountId, {
          card_id: timestamped.id,
          queued: result === 'queued',
        })
        if (result === 'saved') sendNative('haptic', { style: 'light' })
        setSyncMessage(result === 'saved' ? t('saved') : t('queued'))
      } catch {
        setSyncMessage(t('saveFailed'))
      }
      window.setTimeout(() => setSyncMessage(null), 1600)
    }
  }

  async function importGuestCards() {
    if (!guestImport || guestImportInFlight.current === guestImport.accountId) return
    guestImportInFlight.current = guestImport.accountId
    const inventory = readGuestInventory(),
      entries = Object.entries(inventory)
    setSyncMessage(t('importingGuest', { count: entries.length }))
    try {
      for (const [id, state] of entries)
        await saveCard(guestImport.accountId, { id: Number(id), ...state })
      clearGuestInventory()
      guestImportInFlight.current = null
      setGuestImport(null)
      setSyncMessage(t('guestImported', { count: entries.length }))
      setCards(await loadCards(guestImport.accountId, locale))
      window.setTimeout(() => setSyncMessage(null), 2200)
    } catch {
      guestImportInFlight.current = null
      setGuestImport((current) => (current ? { ...current, automatic: false } : current))
      setSyncMessage(t('guestImportFailed'))
    }
  }

  useEffect(() => {
    if (guestImport?.automatic) void importGuestCards()
  }, [guestImport?.accountId, guestImport?.automatic])

  if (blockedReason)
    return (
      <main className="fatal-error">
        <h1>Clash Cards</h1>
        <p>{t(blockedReason === 'maintenance' ? 'maintenance' : 'upgradeRequired')}</p>
        <button onClick={() => window.location.reload()}>{t('retry')}</button>
      </main>
    )
  return (
    <div className="app-shell">
      <header className="topbar">
        <button className="brand" onClick={() => navigate('collection')} aria-label="Clash Cards">
          <Compass className="brand-mark" />
          <span>Clash Cards</span>
        </button>
        <AccountSwitcher />
      </header>

      <main id="top">
        {syncMessage && (
          <div className="sync-toast" role="status">
            {syncMessage}
          </div>
        )}
        {guestImport && !guestImport.automatic && (
          <aside className="import-banner">
            <div>
              <strong>{t('guestProgressFound')}</strong>
              <p>
                {t('guestProgressFoundHint', {
                  name: guestImport.accountName ?? t('thisAccount'),
                })}
              </p>
            </div>
            <button onClick={importGuestCards}>{t('importNow')}</button>
            <button
              className="dismiss"
              aria-label={t('close')}
              onClick={() => setGuestImport(null)}
            >
              ×
            </button>
          </aside>
        )}
        {tab === 'collection' && (
          <Collection
            loading={cardsLoading}
            cards={filtered}
            total={cards.length}
            collected={collected}
            recentCard={recentCard}
            category={category}
            query={query}
            statusFilter={statusFilter}
            onCategory={setCategory}
            onQuery={setQuery}
            onStatus={setStatusFilter}
            onProgress={() => navigate('progress')}
            onAdjust={(card, field, delta) =>
              void updateCard({
                ...card,
                [field]: Math.max(
                  field === 'level' ? 1 : 0,
                  Math.min(field === 'level' ? 99 : 999, card[field] + delta),
                ),
              })
            }
          />
        )}
        {tab === 'trade' && (
          <Trade
            cards={cards}
            activeAccountId={activeAccountId}
            demoMode={demoMode}
            guestMode={guestMode}
            onSignIn={openSignIn}
            refreshSignal={tradeRefresh}
          />
        )}
        {tab === 'progress' && <Progress cards={cards} />}
        {tab === 'profile' && <Profile nativeBridgeReady={nativeBridgeReady} />}
      </main>

      <nav className="bottom-nav" aria-label={t('navigation')}>
        <Nav
          active={tab === 'collection'}
          label={t('collection')}
          icon={<LibraryBig />}
          onClick={() => navigate('collection')}
        />
        <Nav
          active={tab === 'trade'}
          label={t('trade')}
          icon={<Handshake />}
          onClick={() => navigate('trade')}
        />
        <Nav
          active={tab === 'progress'}
          label={t('progress')}
          icon={<BookOpen />}
          onClick={() => navigate('progress')}
        />
        <Nav
          active={tab === 'profile'}
          label={t('profile')}
          icon={<UserRound />}
          onClick={() => navigate('profile')}
        />
      </nav>
    </div>
  )
}

function Nav({
  active,
  label,
  icon,
  onClick,
}: {
  active: boolean
  label: string
  icon: React.ReactNode
  onClick: () => void
}) {
  return (
    <button className={active ? 'active' : ''} onClick={onClick}>
      {icon}
      <span>{label}</span>
    </button>
  )
}

function Collection({
  loading,
  cards,
  total,
  collected,
  recentCard,
  category,
  query,
  statusFilter,
  onCategory,
  onQuery,
  onStatus,
  onProgress,
  onAdjust,
}: {
  loading: boolean
  cards: CardItem[]
  total: number
  collected: number
  recentCard?: CardItem
  category: string
  query: string
  statusFilter: 'all' | 'owned' | 'missing' | 'dupe'
  onCategory: (value: string) => void
  onQuery: (value: string) => void
  onStatus: (value: 'all' | 'owned' | 'missing' | 'dupe') => void
  onProgress: () => void
  onAdjust: (card: CardItem, field: 'owned' | 'wanted' | 'keep' | 'level', delta: number) => void
}) {
  const { t } = useI18n()
  const [entryField, setEntryField] = useState<'owned' | 'wanted' | 'keep' | 'level'>('owned')
  const categories = [
    { value: 'all', label: t('all') },
    { value: 'Elixir Cards', label: t('elixir') },
    { value: 'Dark Elixir Cards', label: t('darkElixir') },
    { value: 'Builder Base Cards', label: t('builderBase') },
    { value: 'Super Troop Cards', label: t('superTroops') },
  ]
  return (
    <>
      <section className="season">
        <div>
          <p className="eyebrow">{t('currentAlbum')}</p>
          <h1>Clashiversary 14</h1>
          <p>{t('eventCards', { count: 60 })}</p>
        </div>
        <button className="text-button" onClick={onProgress}>
          {t('viewDetails')}
        </button>
      </section>
      <section className="progress-card" aria-labelledby="progress-title">
        <div className="section-heading">
          <h2 id="progress-title">{t('albumProgress')}</h2>
          <strong>
            {Math.round((collected / Math.max(total, 1)) * 100)}% <span>{t('complete')}</span>
          </strong>
        </div>
        <div className="progress-track">
          <span style={{ width: `${(collected / Math.max(total, 1)) * 100}%` }} />
        </div>
        <div className="milestones">
          <span className="done">25%</span>
          <span>50%</span>
          <span>75%</span>
          <span>100%</span>
        </div>
      </section>
      <section className="album">
        <div className="section-heading dark">
          <div>
            <p className="eyebrow gold">{t('collectionSection')}</p>
            <h2>
              {category === 'all'
                ? t('allCards')
                : categories.find((item) => item.value === category)?.label}
            </h2>
          </div>
          <span>
            {collected} / {total}
          </span>
        </div>
        {recentCard && (
          <p className="recent-update">{t('recentUpdate', { name: recentCard.name })}</p>
        )}
        <div className="collection-tools">
          <label>
            <Search />
            <input
              value={query}
              onChange={(e) => onQuery(e.target.value)}
              placeholder={t('searchCards')}
            />
          </label>
        </div>
        <div className="entry-field" aria-label={t('editQuantity')}>
          <span>{t('editing')}</span>
          {(['owned', 'wanted', 'keep', 'level'] as const).map((field) => (
            <button
              className={entryField === field ? 'active' : ''}
              key={field}
              onClick={() => setEntryField(field)}
            >
              {t(
                field === 'owned'
                  ? 'ownCount'
                  : field === 'wanted'
                    ? 'wantedCount'
                    : field === 'keep'
                      ? 'keepCount'
                      : 'cardLevel',
              )}
            </button>
          ))}
        </div>
        <div className="status-tabs">
          {(
            [
              ['all', t('allStatus')],
              ['owned', t('owned')],
              ['missing', t('missing')],
              ['dupe', t('duplicates')],
            ] as const
          ).map(([value, label]) => (
            <button
              className={statusFilter === value ? 'active' : ''}
              key={value}
              onClick={() => onStatus(value)}
            >
              {label}
            </button>
          ))}
        </div>
        <div className="category-tabs" role="tablist">
          {categories.map((item) => (
            <button
              className={category === item.value ? 'active' : ''}
              role="tab"
              key={item.value}
              onClick={() => onCategory(item.value)}
            >
              {item.label}
            </button>
          ))}
        </div>
        {loading ? (
          <div className="card-grid" aria-label={t('loading')}>
            {Array.from({ length: 8 }, (_, index) => (
              <span className="card-skeleton" key={index} />
            ))}
          </div>
        ) : cards.length ? (
          <div className="card-grid">
            {cards.map((card) => {
              const value = card[entryField],
                minimum = entryField === 'level' ? 1 : 0
              return (
                <article className={`collectible ${card.owned ? '' : 'missing'}`} key={card.id}>
                  <button
                    className="card-tap"
                    onClick={() => onAdjust(card, entryField, 1)}
                    aria-label={t('increase', { label: card.name })}
                  >
                    <span className="card-number">{card.id}</span>
                    {card.imagePath && (
                      <img
                        className="card-art"
                        src={card.imagePath}
                        alt={card.name}
                        loading="lazy"
                        decoding="async"
                      />
                    )}
                    <span className="card-name">{card.name}</span>
                  </button>
                  <div className="inline-count">
                    <button
                      disabled={value === minimum}
                      onClick={() => onAdjust(card, entryField, -1)}
                      aria-label={t('decrease', { label: card.name })}
                    >
                      −
                    </button>
                    <strong>
                      {entryField === 'owned' ? '×' : ''}
                      {value}
                    </strong>
                    <button
                      disabled={entryField === 'level' && value === 99}
                      onClick={() => onAdjust(card, entryField, 1)}
                      aria-label={t('increase', { label: card.name })}
                    >
                      ＋
                    </button>
                  </div>
                </article>
              )
            })}
          </div>
        ) : (
          <div className="empty-state">
            <strong>{t('noCards')}</strong>
            <p>{t('clearFilters')}</p>
          </div>
        )}
      </section>
    </>
  )
}

function Trade({
  cards,
  activeAccountId,
  demoMode,
  guestMode,
  onSignIn,
  refreshSignal,
}: {
  cards: CardItem[]
  activeAccountId: string | null
  demoMode: boolean
  guestMode: boolean
  onSignIn: () => void
  refreshSignal: number
}) {
  const { locale, t } = useI18n()
  const [mode, setMode] = useState<'need' | 'offer'>('need')
  const [matches, setMatches] = useState<TradeMatch[]>([])
  const [requests, setRequests] = useState<TradeRequest[]>([])
  const [message, setMessage] = useState<string | null>(null)
  const [reportTarget, setReportTarget] = useState<TradeMatch | null>(null)
  const reportDialog = useRef<HTMLDialogElement | null>(null)
  const [reportReason, setReportReason] = useState('spam')
  const [reportDetails, setReportDetails] = useState('')
  useEffect(() => {
    let cancelled = false
    setMatches([])
    setRequests([])
    setMessage(null)
    setReportTarget(null)
    if (!activeAccountId || guestMode || demoMode)
      return () => {
        cancelled = true
      }
    const refresh = () => {
      void listTradeRequests(activeAccountId)
        .then((next) => {
          if (!cancelled) setRequests(next)
        })
        .catch(() => {
          if (!cancelled) setMessage(t('requestsUnavailable'))
        })
    }
    const refreshWhenVisible = () => {
      if (document.visibilityState === 'visible') refresh()
    }
    refresh()
    const refreshTimer = window.setInterval(refresh, 30_000)
    document.addEventListener('visibilitychange', refreshWhenVisible)
    return () => {
      cancelled = true
      window.clearInterval(refreshTimer)
      document.removeEventListener('visibilitychange', refreshWhenVisible)
    }
  }, [activeAccountId, demoMode, guestMode, refreshSignal, t])
  useEffect(() => {
    if (!reportTarget) return
    const close = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setReportTarget(null)
      }
    }
    document.addEventListener('keydown', close)
    window.setTimeout(() => reportDialog.current?.querySelector<HTMLElement>('select')?.focus(), 0)
    return () => document.removeEventListener('keydown', close)
  }, [reportTarget])
  const list =
    mode === 'need' ? cards.filter((c) => c.wanted > 0) : cards.filter((c) => c.owned > c.keep)
  async function search() {
    if (guestMode) {
      onSignIn()
      return
    }
    track('trade_search', locale, activeAccountId)
    if (demoMode) {
      setMatches([
        {
          game_account_id: 'demo',
          display_name: 'Moonlit Clan',
          region_code: 'CN',
          cards_they_offer: [56, 57],
          cards_they_need: [1, 2],
          match_score: 4,
        },
      ])
      return
    }
    if (!activeAccountId) return
    try {
      setMatches(await listTradeMatches(activeAccountId))
      setMessage(null)
    } catch {
      setMessage(t('matchUnavailable'))
    }
  }
  async function send(match: TradeMatch) {
    if (demoMode) {
      setMessage(t('demoSent'))
      return
    }
    if (!activeAccountId) return
    try {
      await createTradeRequest(activeAccountId, match.game_account_id)
      track('trade_request_sent', locale, activeAccountId)
      setMessage(t('requestSent'))
      setRequests(await listTradeRequests(activeAccountId))
    } catch {
      setMessage(t('requestFailed'))
    }
  }
  async function respond(request: TradeRequest, status: 'accepted' | 'declined') {
    try {
      await respondTradeRequest(request.id, status)
      track('trade_request_responded', locale, activeAccountId, { status })
      if (activeAccountId) setRequests(await listTradeRequests(activeAccountId))
    } catch {
      setMessage(t('requestHandleFailed'))
    }
  }
  async function cancel(request: TradeRequest) {
    if (demoMode) {
      setRequests((current) =>
        current.map((item) => (item.id === request.id ? { ...item, status: 'cancelled' } : item)),
      )
      return
    }
    try {
      await cancelTradeRequest(request.id)
      setRequests(await listTradeRequests(activeAccountId!))
      setMessage(t('requestCancelled'))
    } catch {
      setMessage(t('requestCancelFailed'))
    }
  }
  async function reveal(request: TradeRequest) {
    try {
      const contact = await revealTradeContact(request.id)
      setMessage(
        contact
          ? `${contact.account_name}: ${contact.contact_method} · ${contact.contact_value}`
          : t('noContact'),
      )
    } catch {
      setMessage(t('contactUnavailable'))
    }
  }
  async function submitReport() {
    if (!reportTarget || !activeAccountId) return
    if (demoMode) {
      setMessage(t('reportSubmitted'))
      setReportTarget(null)
      return
    }
    try {
      await createUserReport(
        activeAccountId,
        reportTarget.game_account_id,
        reportReason,
        reportDetails,
      )
      setMessage(t('reportSubmitted'))
      setReportTarget(null)
      setReportDetails('')
    } catch {
      setMessage(t('reportFailed'))
    }
  }
  return (
    <>
      <section className="page-section">
        <p className="eyebrow gold">{t('tradeEyebrow')}</p>
        <h1>{t('tradeCenter')}</h1>
        <p className="page-intro">{t('tradePrivacy')}</p>
        <div className="trade-toggle">
          <button className={mode === 'need' ? 'active' : ''} onClick={() => setMode('need')}>
            {t('iNeed')}
          </button>
          <button className={mode === 'offer' ? 'active' : ''} onClick={() => setMode('offer')}>
            {t('iOffer')}
          </button>
        </div>
        {list.length ? (
          <div className="trade-list">
            {list.map((card) => (
              <article key={card.id}>
                <span className="account-symbol">{card.id}</span>
                <div>
                  <strong>{card.name}</strong>
                  <small>
                    {mode === 'need'
                      ? t('needCount', { count: card.wanted })
                      : t('offerCount', { count: card.owned - card.keep })}
                  </small>
                </div>
                <Check />
              </article>
            ))}
          </div>
        ) : (
          <div className="trade-empty">
            <strong>{t(mode === 'need' ? 'noWantedCards' : 'noOfferCards')}</strong>
            <p>{t(mode === 'need' ? 'noWantedCardsHint' : 'noOfferCardsHint')}</p>
          </div>
        )}
        <button
          className="primary-action"
          onClick={search}
          disabled={!guestMode && list.length === 0}
        >
          {guestMode ? t('signInToMatch') : t('findPlayers')}
        </button>
        {message && (
          <p className="profile-message" role="status">
            {message}
          </p>
        )}
        <div className="match-list">
          {matches.map((match) => (
            <div className="match-card" key={match.game_account_id}>
              <span className="match-score">{match.match_score}</span>
              <div>
                <strong>{match.display_name}</strong>
                <p>
                  {matchQuality(match.match_score, t)} · {matchActivity(match.last_active_at, t)}
                  <br />
                  {t('complementary', {
                    count: match.match_score,
                    region: match.region_code || t('regionHidden'),
                  })}
                </p>
                <button className="report-link" onClick={() => setReportTarget(match)}>
                  {t('report')}
                </button>
              </div>
              <button onClick={() => send(match)}>{t('sendRequest')}</button>
            </div>
          ))}
        </div>
        {requests.length > 0 && (
          <>
            <h2 className="subheading">{t('contactRequests')}</h2>
            <div className="request-list">
              {requests.map((request) => (
                <article key={request.id}>
                  <div>
                    <strong>{request.other_account_name}</strong>
                    <small>
                      {request.direction === 'sent' ? t('sent') : t('received')} ·{' '}
                      {t(
                        (
                          {
                            pending: 'statusPending',
                            accepted: 'statusAccepted',
                            declined: 'statusDeclined',
                            cancelled: 'statusCancelled',
                          } as Record<string, string>
                        )[request.status] ?? request.status,
                      )}
                    </small>
                  </div>
                  {request.direction === 'received' && request.status === 'pending' && (
                    <span>
                      <button onClick={() => respond(request, 'accepted')}>{t('accept')}</button>
                      <button onClick={() => respond(request, 'declined')}>{t('decline')}</button>
                    </span>
                  )}
                  {request.direction === 'sent' && request.status === 'pending' && (
                    <button onClick={() => void cancel(request)}>{t('cancelRequest')}</button>
                  )}
                  {request.status === 'accepted' && (
                    <button onClick={() => reveal(request)}>{t('viewContact')}</button>
                  )}
                </article>
              ))}
            </div>
          </>
        )}
      </section>
      {reportTarget && (
        <div className="modal-scrim">
          <dialog ref={reportDialog} className="card-dialog" open aria-modal="true">
            <div className="dialog-heading">
              <h2>{t('reportPlayer', { name: reportTarget.display_name })}</h2>
              <button onClick={() => setReportTarget(null)} aria-label={t('close')}>
                ×
              </button>
            </div>
            <label className="report-field">
              {t('reportReason')}
              <select value={reportReason} onChange={(e) => setReportReason(e.target.value)}>
                <option value="spam">{t('reasonSpam')}</option>
                <option value="harassment">{t('reasonHarassment')}</option>
                <option value="fraud">{t('reasonFraud')}</option>
                <option value="inappropriate">{t('reasonInappropriate')}</option>
                <option value="other">{t('reasonOther')}</option>
              </select>
            </label>
            <label className="report-field">
              {t('reportDetails')}
              <textarea
                maxLength={500}
                value={reportDetails}
                onChange={(e) => setReportDetails(e.target.value)}
              />
            </label>
            <button className="primary-action" onClick={submitReport}>
              {t('submitReport')}
            </button>
          </dialog>
        </div>
      )}
    </>
  )
}

function Progress({ cards }: { cards: CardItem[] }) {
  const { t } = useI18n()
  const groups = useMemo(
    () =>
      [
        ['Elixir Cards', t('elixir')],
        ['Dark Elixir Cards', t('darkElixir')],
        ['Builder Base Cards', t('builderBase')],
        ['Super Troop Cards', t('superTroops')],
      ].map(([value, name]) => {
        const subset = cards.filter((c) => c.category === value)
        return {
          name,
          done: subset.filter((c) => c.owned).length,
          total: subset.length,
        }
      }),
    [cards, t],
  )
  const collected = cards.filter((card) => card.owned > 0).length,
    total = cards.length,
    percent = Math.round((collected / Math.max(total, 1)) * 100),
    next = [25, 50, 75, 100].find((value) => value > percent) ?? 100,
    nextCount = Math.max(0, Math.ceil((total * next) / 100) - collected)
  return (
    <section className="page-section">
      <p className="eyebrow gold">{t('progressEyebrow')}</p>
      <h1>{t('collectionProgress')}</h1>
      <div className="milestone-panel">
        <strong>{t('seasonMilestone')}</strong>
        <p>
          {percent === 100
            ? t('completedAlbum')
            : t('nextMilestone', { count: nextCount, percent: next })}
        </p>
        <div className="progress-track">
          <span style={{ width: `${percent}%` }} />
        </div>
      </div>
      <div className="progress-list">
        {groups.map((group) => (
          <article key={group.name}>
            <div>
              <strong>{group.name}</strong>
              <small>
                {group.done} / {group.total}
              </small>
            </div>
            <progress value={group.done} max={Math.max(group.total, 1)} />
          </article>
        ))}
      </div>
      <div className="weekly-goal">
        <Trophy className="goal-mark" />
        <div>
          <strong>{t('currentCollection')}</strong>
          <p>{t('updatesCount')}</p>
        </div>
        <span className="goal-count">
          {collected} / {total}
        </span>
      </div>
    </section>
  )
}

function Profile({ nativeBridgeReady }: { nativeBridgeReady: boolean }) {
  const { locale, setLocale, t } = useI18n()
  const { session, ready, openSignIn } = useAuth()
  const demoMode = new URLSearchParams(window.location.search).get('demo') === '1'
  const [profile, setProfile] = useState<UserProfile>({
    contact_method: 'wechat',
    contact_value: '',
    contact_sharing_enabled: false,
    notifications_enabled: false,
    locale,
  })
  const [message, setMessage] = useState<string | null>(null)
  useEffect(() => {
    if (!demoMode && ready && session)
      void getProfile()
        .then((value) => {
          if (!value) return
          setProfile(value)
          if (value.locale === 'en' || value.locale === 'zh-CN' || value.locale === 'zh-TW')
            setLocale(value.locale)
          if (value.notifications_enabled) sendNative('sync_push_token')
        })
        .catch(() => setMessage(t('profileReadFailed')))
  }, [demoMode, ready, session, setLocale, t])
  useEffect(() => {
    if (!nativeBridgeReady || demoMode || !session) return
    const listener = (event: Event) => {
      const detail = (event as CustomEvent<{ type?: string; payload?: { granted?: boolean } }>)
        .detail
      if (detail.type !== 'push_permission' || detail.payload?.granted !== false) return
      void clearDeviceTokens().catch(() => undefined)
      setProfile((current) => {
        const next = { ...current, notifications_enabled: false }
        void saveProfile(next).catch(() => undefined)
        return next
      })
      setMessage(t('notificationPermissionDenied'))
    }
    window.addEventListener('clashcards:native', listener)
    return () => window.removeEventListener('clashcards:native', listener)
  }, [demoMode, nativeBridgeReady, session, t])
  async function share() {
    if (hasNativeBridge()) {
      sendNative('share', { text: t('shareText'), url: location.href })
      return
    }
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'Clash Cards',
          text: t('shareText'),
          url: location.href,
        })
        return
      }
      await navigator.clipboard.writeText(location.href)
      setMessage(t('linkCopied'))
    } catch (error) {
      if ((error as DOMException)?.name !== 'AbortError') setMessage(t('shareFailed'))
    }
  }
  function changeLocale(next: Locale) {
    setLocale(next)
    setProfile((current) => ({ ...current, locale: next }))
    track('language_changed', next, null, { locale: next })
  }
  async function persist() {
    if (profile.contact_sharing_enabled && !profile.contact_value?.trim()) {
      setMessage(t('contactRequired'))
      return
    }
    if (demoMode) {
      setMessage(t('demoSettings'))
      return
    }
    try {
      await saveProfile({ ...profile, locale })
      if (profile.notifications_enabled) sendNative('request_push_token')
      else {
        await clearDeviceTokens()
        sendNative('disable_push')
      }
      setMessage(t('settingsSaved'))
    } catch {
      setMessage(t('saveFailed'))
    }
  }
  async function downloadExport() {
    try {
      const data = demoMode ? { profile, demo: true } : await exportUserData()
      track('data_exported', locale)
      const json = JSON.stringify(data, null, 2)
      if (hasNativeBridge()) {
        sendNative('export_json', {
          contents: json,
          filename: 'clash-cards-export.json',
        })
        setMessage(t('exportReady'))
        return
      }
      const url = URL.createObjectURL(new Blob([json], { type: 'application/json' }))
      const a = document.createElement('a')
      a.href = url
      a.download = 'clash-cards-export.json'
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      setMessage(t('exportFailed'))
    }
  }
  async function removeAccount() {
    if (!window.confirm(t('deleteConfirm'))) return
    if (demoMode) {
      setMessage(t('demoNoDelete'))
      return
    }
    try {
      const userId = session?.user.id
      await deleteUserAccount()
      sendNative('disable_push')
      if (userId) clearInventoryQueueForUser(userId)
      localStorage.removeItem('clash_cards_active_account_id')
      clearGuestInventory()
    } catch {
      setMessage(t('deleteFailed'))
    }
  }
  async function signOut() {
    const userId = session?.user.id
    if (hasNativeBridge()) {
      const response = await sendNativeAndWait('disable_push', {}, 'push_token_revoked')
      const token = response?.token
      if (typeof token === 'string') {
        try {
          await unregisterDeviceToken(token)
        } catch {
          // Continue local sign-out; Firebase has already invalidated this token.
        }
      }
    }
    if (userId) clearInventoryQueueForUser(userId)
    await supabase.auth.signOut({ scope: 'local' })
  }
  if (ready && !session && !demoMode)
    return (
      <section className="page-section">
        <p className="eyebrow gold">{t('profileEyebrow')}</p>
        <h1>{t('profile')}</h1>
        <div className="profile-form">
          <label>
            {t('language')}
            <select value={locale} onChange={(e) => changeLocale(e.target.value as Locale)}>
              <option value="en">{t('languageEnglish')}</option>
              <option value="zh-CN">{t('languageSimplified')}</option>
              <option value="zh-TW">{t('languageTraditional')}</option>
            </select>
          </label>
          <p>{t('guestDataHint')}</p>
          <button className="primary-action" onClick={openSignIn}>
            {t('signInSync')}
          </button>
        </div>
        {message && (
          <p className="profile-message" role="status">
            {message}
          </p>
        )}
        <div className="settings-list">
          <button onClick={share}>
            <Share2 />
            {t('shareAlbum')}
            <span>→</span>
          </button>
          <a href="/help.html" target="_blank" rel="noopener noreferrer">
            {t('help')}
            <span>→</span>
          </a>
          <a href="/privacy.html" target="_blank" rel="noopener noreferrer">
            {t('privacy')}
            <span>→</span>
          </a>
          <a href="/terms.html" target="_blank" rel="noopener noreferrer">
            {t('terms')}
            <span>→</span>
          </a>
        </div>
      </section>
    )
  return (
    <section className="page-section">
      <p className="eyebrow gold">{t('profileEyebrow')}</p>
      <h1>{t('profile')}</h1>
      <div className="profile-form">
        <label>
          {t('language')}
          <select value={locale} onChange={(e) => changeLocale(e.target.value as Locale)}>
            <option value="en">{t('languageEnglish')}</option>
            <option value="zh-CN">{t('languageSimplified')}</option>
            <option value="zh-TW">{t('languageTraditional')}</option>
          </select>
        </label>
        <label>
          {t('contactType')}
          <select
            value={profile.contact_method ?? ''}
            onChange={(e) => setProfile({ ...profile, contact_method: e.target.value || null })}
          >
            <option value="wechat">WeChat</option>
            <option value="discord">Discord</option>
            <option value="telegram">Telegram</option>
            <option value="email">Email</option>
            <option value="other">{t('contactOther')}</option>
          </select>
        </label>
        <label>
          {t('contact')}
          <input
            value={profile.contact_value ?? ''}
            onChange={(e) => setProfile({ ...profile, contact_value: e.target.value })}
            maxLength={160}
            placeholder={t('contactPlaceholder')}
          />
        </label>
        <label className="toggle-row">
          <input
            type="checkbox"
            checked={profile.contact_sharing_enabled}
            onChange={(e) =>
              setProfile({
                ...profile,
                contact_sharing_enabled: e.target.checked,
              })
            }
          />
          {t('shareContact')}
        </label>
        {nativeBridgeReady && (
          <label className="toggle-row">
            <input
              type="checkbox"
              checked={profile.notifications_enabled}
              onChange={(e) =>
                setProfile({
                  ...profile,
                  notifications_enabled: e.target.checked,
                })
              }
            />
            {t('notifications')}
          </label>
        )}
        <button className="primary-action" onClick={persist}>
          {t('saveSettings')}
        </button>
      </div>
      {message && (
        <p className="profile-message" role="status">
          {message}
        </p>
      )}
      <div className="settings-list">
        <button onClick={share}>
          <Share2 />
          {t('shareAlbum')}
          <span>→</span>
        </button>
        <button onClick={downloadExport}>
          {t('exportData')}
          <span>↓</span>
        </button>
        <a href="/help.html" target="_blank" rel="noopener noreferrer">
          {t('help')}
          <span>→</span>
        </a>
        <a href="/privacy.html" target="_blank" rel="noopener noreferrer">
          {t('privacy')}
          <span>→</span>
        </a>
        <a href="/terms.html" target="_blank" rel="noopener noreferrer">
          {t('terms')}
          <span>→</span>
        </a>
        <button onClick={() => void signOut()}>
          {t('signOut')}
          <span>→</span>
        </button>
        <button className="danger" onClick={removeAccount}>
          {t('deleteAccount')}
        </button>
      </div>
    </section>
  )
}
